import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { TourProvider } from './components/tour';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import EvidencePage from './pages/EvidencePage';
import RisksPage from './pages/RisksPage';
import PoliciesPage from './pages/PoliciesPage';
import ControlsPage from './pages/ControlsPage';
import FrameworksPage from './pages/FrameworksPage';
import ConnectorsPage from './pages/ConnectorsPage';
import AuditPortalPage from './pages/AuditPortalPage';
import ControlMappingPage from './pages/ControlMappingPage';
import CustomFrameworkPage from './pages/CustomFrameworkPage';
import BulkImportPage from './pages/BulkImportPage';
import ComplianceMetricsPage from './pages/ComplianceMetricsPage';
import TeamsIntegrationPage from './pages/TeamsIntegrationPage';
import SlackIntegrationPage from './pages/SlackIntegrationPage';
import SAMLSSOPage from './pages/SAMLSSOPage';
import ComplianceDriftPage from './pages/ComplianceDriftPage';
import SchedulerPage from './pages/SchedulerPage';
import AdminPage from './pages/AdminPage';
import DemoModePage from './pages/DemoModePage';
// Vulnerability Management
import VulnerabilityDashboardPage from './pages/VulnerabilityDashboardPage';
import VulnerabilityScannerPage from './pages/VulnerabilityScannerPage';
import ContinuousMonitoringPage from './pages/ContinuousMonitoringPage';
// New Service Pages
import SOC2ReadinessPage from './pages/SOC2ReadinessPage';
import ThreatIntelPage from './pages/ThreatIntelPage';
import BillingPage from './pages/BillingPage';
import IntegrationsPage from './pages/IntegrationsPage';
import IntegrationWizardPage from './pages/IntegrationWizardPage';
import IntegrationHealthPage from './pages/IntegrationHealthPage';
import AdvancedReportingPage from './pages/AdvancedReportingPage';
import CTEMPage from './pages/CTEMPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Marketing Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Onboarding (Protected but separate from main app) */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/evidence" element={<ProtectedRoute><EvidencePage /></ProtectedRoute>} />
      <Route path="/risks" element={<ProtectedRoute><RisksPage /></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute><PoliciesPage /></ProtectedRoute>} />
      <Route path="/controls" element={<ProtectedRoute><ControlsPage /></ProtectedRoute>} />
      <Route path="/frameworks" element={<ProtectedRoute><FrameworksPage /></ProtectedRoute>} />
      <Route path="/connectors" element={<ProtectedRoute><ConnectorsPage /></ProtectedRoute>} />
      <Route path="/audit-portal" element={<ProtectedRoute><AuditPortalPage /></ProtectedRoute>} />
      <Route path="/control-mapping" element={<ProtectedRoute><ControlMappingPage /></ProtectedRoute>} />
      <Route path="/custom-frameworks" element={<ProtectedRoute><CustomFrameworkPage /></ProtectedRoute>} />
      <Route path="/bulk-import" element={<ProtectedRoute><BulkImportPage /></ProtectedRoute>} />
      <Route path="/compliance-metrics" element={<ProtectedRoute><ComplianceMetricsPage /></ProtectedRoute>} />
      <Route path="/teams-integration" element={<ProtectedRoute><TeamsIntegrationPage /></ProtectedRoute>} />
      <Route path="/slack-integration" element={<ProtectedRoute><SlackIntegrationPage /></ProtectedRoute>} />
      <Route path="/saml-sso" element={<ProtectedRoute><SAMLSSOPage /></ProtectedRoute>} />
      <Route path="/compliance-drift" element={<ProtectedRoute><ComplianceDriftPage /></ProtectedRoute>} />
      <Route path="/scheduler" element={<ProtectedRoute><SchedulerPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/demo" element={<ProtectedRoute><DemoModePage /></ProtectedRoute>} />
      
      {/* Vulnerability Management */}
      <Route path="/vulnerabilities" element={<ProtectedRoute><VulnerabilityDashboardPage /></ProtectedRoute>} />
      <Route path="/vulnerability-scanner" element={<ProtectedRoute><VulnerabilityScannerPage /></ProtectedRoute>} />
      <Route path="/continuous-monitoring" element={<ProtectedRoute><ContinuousMonitoringPage /></ProtectedRoute>} />
      
      {/* New Service Pages */}
      <Route path="/soc2" element={<ProtectedRoute><SOC2ReadinessPage /></ProtectedRoute>} />
      <Route path="/threat-intel" element={<ProtectedRoute><ThreatIntelPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/integration-wizard" element={<ProtectedRoute><IntegrationWizardPage /></ProtectedRoute>} />
      <Route path="/integration-health" element={<ProtectedRoute><IntegrationHealthPage /></ProtectedRoute>} />
      <Route path="/advanced-reporting" element={<ProtectedRoute><AdvancedReportingPage /></ProtectedRoute>} />
      <Route path="/ctem" element={<ProtectedRoute><CTEMPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <WebSocketProvider>
          <TourProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </TourProvider>
        </WebSocketProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
